/**
 * Chart colours come from CSS custom properties defined by the app's design tokens
 * (apps/web/src/app/styles/tokens.css), so light/dark switching needs no chart code.
 *
 * The categorical slots are a validated colour-blind-safe order (adjacent-pair CVD ΔE ≥ 8.4
 * in both themes). Assign them in fixed order and never cycle: a 9th variant folds into
 * "Other" or small multiples. Percentile fans reuse the variant hue with opacity.
 */
export const SERIES_COUNT = 8;

export interface ChartTheme {
  series: readonly string[];
  divergingPositive: string;
  divergingNegative: string;
  divergingMid: string;
  grid: string;
  axis: string;
  axisLabel: string;
  crosshair: string;
  surface: string;
  critical: string;
}

export const CHART_CSS_VARS = {
  series: Array.from({ length: SERIES_COUNT }, (_, i) => `--chart-series-${String(i + 1)}`),
  divergingPositive: '--chart-diverging-pos',
  divergingNegative: '--chart-diverging-neg',
  divergingMid: '--chart-diverging-mid',
  grid: '--chart-grid',
  axis: '--chart-axis',
  axisLabel: '--chart-axis-label',
  crosshair: '--chart-crosshair',
  surface: '--chart-surface',
  critical: '--status-critical',
} as const;

/** Resolves the theme from any CSS-variable reader (e.g. `getComputedStyle(el).getPropertyValue`). */
export function resolveChartTheme(read: (cssVar: string) => string): ChartTheme {
  const get = (name: string): string => read(name).trim();
  return {
    series: CHART_CSS_VARS.series.map(get),
    divergingPositive: get(CHART_CSS_VARS.divergingPositive),
    divergingNegative: get(CHART_CSS_VARS.divergingNegative),
    divergingMid: get(CHART_CSS_VARS.divergingMid),
    grid: get(CHART_CSS_VARS.grid),
    axis: get(CHART_CSS_VARS.axis),
    axisLabel: get(CHART_CSS_VARS.axisLabel),
    crosshair: get(CHART_CSS_VARS.crosshair),
    surface: get(CHART_CSS_VARS.surface),
    critical: get(CHART_CSS_VARS.critical),
  };
}

export function readChartTheme(element: Element): ChartTheme {
  const style = getComputedStyle(element);
  return resolveChartTheme((name) => style.getPropertyValue(name));
}

const CSS_VAR = /var\(\s*(--[\w-]+)\s*(?:,\s*([^()]*?)\s*)?\)/g;

/**
 * Replaces `var(--name)` (and `var(--name, fallback)`) in rendered chart markup with concrete
 * values, so the SVG works without a stylesheet (PDF export, downloads). Values may themselves use
 * variables (`--chart-surface: var(--bg-1)`). Throws on a variable with no value and no fallback,
 * so a missing token can't silently print black.
 */
export function resolveCssVars(markup: string, vars: Readonly<Record<string, string>>): string {
  let out = markup;
  for (let depth = 0; depth < 8 && out.includes('var('); depth++) {
    out = out.replace(CSS_VAR, (_, name: string, fallback: string | undefined) => {
      const value = vars[name] ?? fallback;
      if (value === undefined) throw new Error(`No value for CSS variable ${name}`);
      return value.trim();
    });
  }
  if (out.includes('var(')) throw new Error('CSS variables nest too deeply to resolve');
  return out;
}
